import axios from "axios";
import type { Mentor, MentorPanel } from "../shared/mentorTypes";
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "../app/store";

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonObject | JsonValue[];

interface JsonObject {
  [key: string]: JsonValue;
}

interface EventData {
  userId: string;
  sessionid: string;
  verb: string;
  objecttype: string; // Page, Session, Button,
  /**
     * OBJECT TPYES
     *  Session
        Page (no result)
        Resource (no result)
        Mentor Session. result = {"MentorSession" : id for MentorSession Table entry}
        Mentor Search. result = {"search": "<search>"
        Resource Filter. result = {"search": "filter" (or a list?)}
        Recommended. result = {"recs" : [list of recommendation types and ID's]}
     */
  object: string; // If start session, then session id. If Page objecttype, then should be url of the page
  timestamp: string;
  context?: JsonObject;
  result?: JsonValue;
}

export function pageStartEventBuilder(
  userId: string,
  sessionid: string,
  url?: string,
  context?: JsonObject,
): EventData {
  const pageUrl = window.location.origin + window.location.pathname;
  return {
    userId,
    sessionid,
    verb: "Start",
    objecttype: "Page",
    object: url ?? pageUrl,
    context: {
      ...context,
    },
    timestamp: new Date().toISOString(),
  };
}

export function sessionStartEventBuilder(
  userId: string,
  sessionid: string,
): EventData {
  return {
    userId,
    sessionid,
    verb: "Start",
    objecttype: "Session",
    object: sessionid,
    context: {},
    timestamp: new Date().toISOString(),
  };
}

export function filterInternshipsEventBuilder(
  userId: string,
  sessionid: string,
  resultingResources: JsonValue,
  context: JsonObject,
): EventData {
  return {
    userId,
    sessionid,
    verb: "Filter",
    objecttype: "ResourceFilter",
    object: "Internship",
    result: resultingResources,
    timestamp: new Date().toISOString(),
    context: {
      ...context,
    },
  };
}

export function filterGraduateSchoolsEventBuilder(
  userId: string,
  sessionid: string,
  resultingResources: JsonValue,
  context: JsonObject,
): EventData {
  return {
    userId,
    sessionid,
    verb: "Filter",
    objecttype: "ResourceFilter",
    object: "GraduateSchool",
    result: resultingResources,
    timestamp: new Date().toISOString(),
    context: {
      ...context,
    },
  };
}

export function filterCareersEventBuilder(
  userId: string,
  sessionid: string,
  resultingResources: JsonValue,
  context: JsonObject,
): EventData {
  return {
    userId,
    sessionid,
    verb: "Filter",
    objecttype: "ResourceFilter",
    object: "ExploreCareers",
    result: resultingResources,
    timestamp: new Date().toISOString(),
    context: {
      ...context,
    },
  };
}

export function bookmarkEventBuilder(
  adding: boolean,
  userId: string,
  sessionid: string,
  bookmarkedResourceType: string,
  bookmarkedResourceNameOrId: string,
  context: JsonObject,
): EventData {
  return {
    userId,
    sessionid,
    verb: `${adding ? "Add" : "Remove"} Bookmark`,
    objecttype: bookmarkedResourceType,
    object: bookmarkedResourceNameOrId,
    timestamp: new Date().toISOString(),
    context: {
      ...context,
    },
  };
}

export function mentorSearchEventBuilder(
  userId: string,
  sessionid: string,
  query: string,
  filteredMentors: Mentor[],
  context: JsonObject,
): EventData {
  return {
    userId,
    sessionid,
    verb: "Search",
    objecttype: "MentorSearch",
    object: "NONE",
    result: filteredMentors.map((mentor) => mentor.name),
    timestamp: new Date().toISOString(),
    context: {
      ...context,
      query: query,
    },
  };
}

export function handleButtonClickEventBuilder(
  userId: string,
  sessionid: string,
  buttonName: string,
  context: JsonObject,
): EventData {
  return {
    userId,
    sessionid,
    verb: "Click",
    objecttype: "Button",
    object: buttonName,
    timestamp: new Date().toISOString(),
    context: {
      ...context,
    },
  };
}

export function handleTileClickEventBuilder(
  userId: string,
  sessionid: string,
  tileTitle: string,
  context: JsonObject,
): EventData {
  return {
    userId,
    sessionid,
    verb: "Click",
    objecttype: "Tile",
    object: tileTitle,
    timestamp: new Date().toISOString(),
    context: {
      ...context,
    },
  };
}

export function mentorCardClickEventBuilder(
  userId: string,
  sessionid: string,
  mentor: Mentor,
  context: JsonObject,
): EventData {
  return {
    userId,
    sessionid,
    verb: "Start",
    objecttype: "MentorSession",
    object: sessionid,
    result: mentor.mentorUrl,
    timestamp: new Date().toISOString(),
    context: {
      ...context,
      mentors: [mentor.name],
    },
  };
}

export function mentorPanelClickEventBuilder(
  userId: string,
  sessionid: string,
  panel: MentorPanel,
  panelMentorNames: string[],
  context: JsonObject,
): EventData {
  return {
    userId,
    sessionid,
    verb: "Start",
    objecttype: "MentorSession",
    object: sessionid,
    result: panel.panelUrl,
    timestamp: new Date().toISOString(),
    context: {
      ...context,
      mentors: panelMentorNames,
    },
  };
}

export function completeMentorSessionEventBuilder(
  userId: string,
  sessionid: string,
  mentors: Mentor[],
  context: JsonObject,
): EventData {
  return {
    userId,
    sessionid,
    verb: "Complete",
    objecttype: "MentorSession",
    object: sessionid,
    timestamp: new Date().toISOString(),
    context: {
      ...context,
      mentors: mentors.map((mentor) => mentor.name),
    },
  };
}

export const sendEvent = createAsyncThunk<
  undefined,
  EventData,
  { state: RootState; rejectValue: string }
>("events/sendEvent", async (eventData, thunkApi) => {
  if (!eventData.userId || !eventData.sessionid) return;

  const endpoint =
    import.meta.env.VITE_USER_ROUTE +
    "/" +
    encodeURIComponent(eventData.userId) +
    "/event";

  const pageUrl = window.location.origin + window.location.pathname;
  const recommendations = thunkApi.getState().mentorRecommendations.data;

  try {
    await axios.post(endpoint, {
      ...eventData,
      context: {
        ...(eventData.context ?? {}),
        pageUrl,
        recommendations,
      },
    });
  } catch (error) {
    return thunkApi.rejectWithValue(
      error instanceof Error ? error.message : "Error sending event",
    );
  }
});

export async function sendUserLoginEvent(userId: string) {
  try {
    await axios.post(import.meta.env.VITE_USER_LOGIN, { userId });
  } catch (error) {
    console.error(
      "Error sending user login event:",
      error instanceof Error ? error.message : "Unknown error",
    );
  }
}

export function getEmailFromUserId(userId: string): string {
  return `${userId}@psychologypathways.org`;
}
